import { HttpContext } from '@angular/common/http';
import { Router } from '@angular/router';
import { ReuseTabService } from '@delon/abc/reuse-tab';
import { ALLOW_ANONYMOUS } from '@delon/auth';
import { map, Observable, Subject } from 'rxjs';

import { BaseLogic } from './base.logic';
import { ApplicationLogic } from './application.logic';
import { HttpLogic } from './http.logic';
import { IJsonTable } from '@shared/model';
import { ILoginAttemptResult, parseLoginLockInfo } from '@shared';
import { Md5 } from 'ts-md5';

const LoginApiMap: {
  [key: string]: string;
} = {
  '1': 'admin/v1/session',
  '2': 'corporation/v1/session',
  '6': 'hospital/v1/session',
  '3': 'pharmacy/v1/session',
  '5': 'distributor/v1/session'
};

export class UserLogic extends BaseLogic {
  protected reuseTabService!: ReuseTabService;
  protected router!: Router;
  protected override initialize() {
    super.initialize();
    this.reuseTabService = this.injector.get(ReuseTabService, undefined, { optional: true }) as ReuseTabService;
    this.router = this.injector.get(Router);
  }

  getCaptchaUrl(appType: number): Observable<string> {
    return this.http
      .get(
        '/home/v1/captcha',
        {
          app_type: appType,
          _: Math.random()
        },
        {
          responseType: 'blob',
          context: new HttpContext().set(ALLOW_ANONYMOUS, true)
        }
      )
      .pipe(map((blob: Blob) => window.URL.createObjectURL(blob)));
  }

  /**
   * 用户登录(可能未来存在验证码)
   *
   * @author 王阮强(wangruanqiang@youzhibo.cn)
   * @date 2021-02-25
   * @param {string} account 账号
   * @param {string} password 密码
   * @param {number} [appType=3] 登陆类型
   * @returns 登录结果流，包含成功、普通失败或后端锁定信息
   * @example login('demo', 'hs123456', 6, 'abcd')
   */
  login(account: string, password: string, appType: number = 3, code: any): Observable<ILoginAttemptResult> {
    const loginApi = LoginApiMap[appType.toString()];
    if (!loginApi) {
      throw new Error(`Unsupported login appType: ${appType}`);
    }
    const httpLogic = HttpLogic.getInstance(this.injector);
    this.privilegeSrv.clear();
    this.menuSrv.clear();
    this.aclSrv.setAbility([]);
    const param = {
      account: account,
      password: Md5.hashStr(password),
      code: code
    };
    const { success, error, complete } = httpLogic.reqPost(
      loginApi,
      param,
      {},
      {
        context: new HttpContext().set(ALLOW_ANONYMOUS, true)
      }
    );
    const resultSubject = new Subject<ILoginAttemptResult>();
    let settled = false;
    /**
     * 只向登录页发送一次登录结果。
     *
     * @param result 登录结果
     * @returns 无返回值
     * @example settle({ status: 'success', response, lock: null })
     */
    const settle = (result: ILoginAttemptResult): void => {
      if (settled) return;
      settled = true;
      resultSubject.next(result);
      resultSubject.complete();
    };
    success.subscribe((jResult: IJsonTable) => {
      const functions = {
        ...(jResult.data.function || {})
      };
      // 清空路由复用信息
      // this.reuseTabService.clear();
      // 初始化应用信息
      ApplicationLogic.getInstance(this.injector).load(
        {
          token: jResult.data.token,
          refresh_token: jResult.data.refresh_token,
          expired: +new Date() + (jResult.data.expire_in - 30 * 60) * 1000,
          refresh_expired: +new Date() + (jResult.data.expire_in - 5 * 60) * 1000,
          expire_in: jResult.data.expire_in,
          top_level: jResult.data.top_level,
          refresh_expire_in: jResult.data.refresh_expire_in
        },
        {
          user: jResult.data.user,
          menu: jResult.data.menu,
          functions,
          appType: appType
        }
      );

      if (appType === 6) {
        this.router.navigateByUrl('/hospital/home');
      } else if (appType === 3 || appType === 4) {
        this.router.navigateByUrl('/pharmacy/data_center/normal-prescription');
      } else if (appType === 5) {
        this.router.navigateByUrl('/distributor/workbench/store');
      } else if (appType === 1) {
        this.router.navigateByUrl('/admin/institution/corporation');
      } else {
        ApplicationLogic.getInstance(this.injector).toHome();
      }
      settle({
        status: 'success',
        response: jResult,
        lock: null
      });
    });
    error.subscribe((jResult: IJsonTable) => {
      const lock = parseLoginLockInfo(jResult);
      if (!lock) {
        switch (jResult.state) {
          case 77: // 医院状态异常-过期或者未开启
          case 75: // 登录方式无权访
          case 71: // 功能编码没查到无权访问
          case 751: // 医院状态异常-过期或者未开启
            this.msgSrv.error(`${jResult.msg}[${jResult.state}]`);
            break;
          default:
            this.msgSrv.error(`${jResult.msg}[${jResult.state}]`);
            break;
        }
      }
      settle({
        status: 'error',
        response: jResult,
        lock
      });
    });
    complete.subscribe(() => {
      if (!settled) {
        settled = true;
        resultSubject.complete();
      }
    });
    return resultSubject.asObservable();
  }
}
